import type { LicitacoesFilters, LicitacoesFiltrosPersistiveis } from '@/hooks/useLicitacoes';
import { REGIOES_LABELS, REGIOES_UFS_FALLBACK } from '@/lib/licitacoesOpcoes';

/**
 * Derivação dos "chips de filtro ativo" e normalização dos filtros da busca
 * avançada de licitações.
 *
 * Regras do contrato do backend que vivem aqui:
 *  - `uf`, `ufs_multi` e `regioes` são combinados com AND. A tela usa um
 *    modelo geográfico único (`ufs_multi`); `uf` e `regioes` só aparecem em
 *    buscas salvas antigas e são convertidos para `ufs_multi`.
 *  - `modalidades` (igualdade) e `modalidade` (parcial) nunca convivem.
 *  - `orgao_cnpj` só vai para a API com 14 dígitos.
 */

export interface FiltroAtivo {
  id: string;
  label: string;
  /** Patch aplicado sobre os filtros atuais quando o chip é removido. */
  patch: Partial<LicitacoesFilters>;
}

export const somenteDigitos = (valor: string) => valor.replace(/\D/g, '');

const semVazio = <T,>(lista: T[]) => (lista.length > 0 ? lista : undefined);

const formatarMoeda = (valor: number) =>
  valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatarData = (valor: string) => {
  const data = new Date(`${valor}T00:00:00`);
  return Number.isNaN(data.getTime()) ? valor : data.toLocaleDateString('pt-BR');
};

export const regiaoLabel = (key: string) =>
  REGIOES_LABELS.find((regiao) => regiao.key === key)?.label ?? key;

/**
 * Converte filtros vindos de uma busca salva (ou de uma versão anterior da
 * tela) no modelo único usado hoje, evitando conjuntos geográficos disjuntos.
 */
export function normalizarFiltrosLicitacoes(
  filtros: LicitacoesFilters,
  regioesMap: Record<string, string[]> = REGIOES_UFS_FALLBACK
): LicitacoesFilters {
  const normalizados: LicitacoesFilters = { ...filtros };

  const ufs = new Set(normalizados.ufs_multi || []);
  if (normalizados.uf) {
    ufs.add(normalizados.uf.toUpperCase());
    delete normalizados.uf;
  }
  (normalizados.regioes || []).forEach((regiao) => {
    (regioesMap[regiao] || REGIOES_UFS_FALLBACK[regiao] || []).forEach((item) => ufs.add(item));
  });
  delete normalizados.regioes;
  normalizados.ufs_multi = semVazio([...ufs]);

  if (normalizados.modalidades && normalizados.modalidades.length > 0) {
    delete normalizados.modalidade;
  }

  if (normalizados.orgao_cnpj) {
    const digitos = somenteDigitos(normalizados.orgao_cnpj);
    if (digitos.length === 14) normalizados.orgao_cnpj = digitos;
    else delete normalizados.orgao_cnpj;
  }

  return normalizados;
}

/** Remove apenas o estado de paginação, deixando o conjunto persistível. */
export function filtrosPersistiveis(filtros: LicitacoesFilters): LicitacoesFiltrosPersistiveis {
  const copia: LicitacoesFilters = { ...filtros };
  delete copia.page;
  delete copia.limit;
  return copia;
}

interface ListarOptions {
  empresaNome?: (id: number) => string;
}

function chipsDeLista(
  key: 'ufs_multi' | 'modalidades' | 'esferas' | 'portal' | 'objeto_inclui' | 'objeto_exclui',
  valores: string[] | undefined,
  prefixo: string,
  rotulo: (valor: string) => string = (valor) => valor
): FiltroAtivo[] {
  return (valores || []).map((valor) => ({
    id: `${key}:${valor}`,
    label: `${prefixo}: ${rotulo(valor)}`,
    patch: { [key]: semVazio((valores || []).filter((item) => item !== valor)) } as Partial<LicitacoesFilters>,
  }));
}

/** Lista legível dos filtros ativos, cada um removível individualmente. */
export function listarFiltrosAtivos(
  filtros: LicitacoesFilters,
  options: ListarOptions = {}
): FiltroAtivo[] {
  const chips: FiltroAtivo[] = [];

  if (filtros.search) {
    chips.push({ id: 'search', label: `Busca: ${filtros.search}`, patch: { search: undefined } });
  }

  chips.push(...chipsDeLista('ufs_multi', filtros.ufs_multi, 'UF'));

  if (filtros.uf) {
    chips.push({ id: 'uf', label: `UF: ${filtros.uf}`, patch: { uf: undefined } });
  }

  (filtros.regioes || []).forEach((regiao) => {
    chips.push({
      id: `regioes:${regiao}`,
      label: `Região: ${regiaoLabel(regiao)}`,
      patch: { regioes: semVazio((filtros.regioes || []).filter((item) => item !== regiao)) },
    });
  });

  if (filtros.municipio) {
    chips.push({ id: 'municipio', label: `Cidade: ${filtros.municipio}`, patch: { municipio: undefined } });
  }

  chips.push(...chipsDeLista('modalidades', filtros.modalidades, 'Modalidade'));

  if (filtros.modalidade) {
    chips.push({
      id: 'modalidade',
      label: `Modalidade contém: ${filtros.modalidade}`,
      patch: { modalidade: undefined },
    });
  }

  chips.push(...chipsDeLista(
    'esferas',
    filtros.esferas,
    'Esfera',
    (valor) => valor.charAt(0).toUpperCase() + valor.slice(1)
  ));

  if (filtros.orgao_nome) {
    chips.push({ id: 'orgao_nome', label: `Órgão: ${filtros.orgao_nome}`, patch: { orgao_nome: undefined } });
  }

  if (filtros.orgao_cnpj) {
    chips.push({ id: 'orgao_cnpj', label: `CNPJ do órgão: ${filtros.orgao_cnpj}`, patch: { orgao_cnpj: undefined } });
  }

  if (filtros.numero_edital) {
    chips.push({ id: 'numero_edital', label: `Nº do edital: ${filtros.numero_edital}`, patch: { numero_edital: undefined } });
  }

  chips.push(...chipsDeLista('portal', filtros.portal, 'Portal'));

  (filtros.empresa_ids || []).forEach((empresaId) => {
    chips.push({
      id: `empresa_ids:${empresaId}`,
      label: `Empresa: ${options.empresaNome?.(empresaId) ?? `#${empresaId}`}`,
      patch: { empresa_ids: semVazio((filtros.empresa_ids || []).filter((item) => item !== empresaId)) },
    });
  });

  chips.push(...chipsDeLista('objeto_inclui', filtros.objeto_inclui, 'Inclui'));
  chips.push(...chipsDeLista('objeto_exclui', filtros.objeto_exclui, 'Exclui'));

  if (filtros.valor_min !== undefined) {
    chips.push({ id: 'valor_min', label: `Valor ≥ ${formatarMoeda(filtros.valor_min)}`, patch: { valor_min: undefined } });
  }
  if (filtros.valor_max !== undefined) {
    chips.push({ id: 'valor_max', label: `Valor ≤ ${formatarMoeda(filtros.valor_max)}`, patch: { valor_max: undefined } });
  }
  if (filtros.data_de) {
    chips.push({ id: 'data_de', label: `Prazo a partir de ${formatarData(filtros.data_de)}`, patch: { data_de: undefined } });
  }
  if (filtros.data_ate) {
    chips.push({ id: 'data_ate', label: `Prazo até ${formatarData(filtros.data_ate)}`, patch: { data_ate: undefined } });
  }

  if (filtros.categoria) {
    chips.push({ id: 'categoria', label: `Categoria IA: ${filtros.categoria}`, patch: { categoria: undefined } });
  }
  if (filtros.relevancia_min !== undefined && filtros.relevancia_min > 0) {
    chips.push({
      id: 'relevancia_min',
      label: `Relevância IA ≥ ${filtros.relevancia_min}%`,
      patch: { relevancia_min: undefined },
    });
  }
  if (filtros.somente_ia) {
    chips.push({ id: 'somente_ia', label: 'Somente processadas por IA', patch: { somente_ia: undefined } });
  }
  if (filtros.pago !== undefined) {
    chips.push({
      id: 'pago',
      label: filtros.pago ? 'Somente portais pagos' : 'Somente portais gratuitos',
      patch: { pago: undefined },
    });
  }
  if (filtros.somente_ativas === false) {
    chips.push({ id: 'somente_ativas', label: 'Incluindo vencidas', patch: { somente_ativas: true } });
  }

  return chips;
}
